import type { AxiosError } from 'axios'
import { INFINITE_SCROLL_PAGE_SIZE } from '../../../shared/config/constants'
import type { ClientDiscogsRelease } from '../../../shared/types/discogs'
import {
	ACTIVITY_TYPES,
	type ActivityFeedItem,
	type ActivityType,
	type FriendsResponse,
} from '../../../shared/types/social'
import { getReleaseDetails } from '../../api/collection'
import { getSessionSignal } from '../../api/requestSession'
import { addFriend, getActivityFeed, updateFriendship } from '../../api/social'
import {
	loadFriendsWithIndicator,
	renderFriendRequestIndicator,
	subscribeFriendsUpdates,
} from '../../components/FriendRequestIndicator'
import { bindInfoTooltips } from '../../components/InfoTooltip'
import {
	bindMultiSelectOutsideDismiss,
	getNextMultiSelectSelection,
	handleMultiSelectEscape,
	handleMultiSelectNavigation,
	syncMultiSelect,
	toggleMultiSelect,
} from '../../components/inputs/MultiSelect'
import { closeOverlay, openOverlay } from '../../components/Overlay'
import { renderReleaseLazy } from '../../components/release/Release'
import { getReleaseUpdateDetail } from '../../components/release/releaseEvents'
import { createInfiniteList } from '../../utils/infiniteList'
import {
	activityEmptyTemplate,
	activityErrorTemplate,
	activityItemTemplate,
	activityLoadingTemplate,
	activityScopeButtonTemplate,
	friendsListsTemplate,
	friendsOverlayErrorTemplate,
	friendsOverlayLoadingTemplate,
	friendsOverlayTemplate,
} from './activityPageTemplates'

export class ActivityPageController {
	private readonly rootEl: HTMLElement
	private readonly sessionSignal = getSessionSignal()
	private readonly listEl: HTMLOListElement
	private readonly statusEl: HTMLElement
	private readonly infiniteList: ReturnType<
		typeof createInfiniteList<ActivityFeedItem>
	>
	private readonly noFriendsEl: HTMLElement
	private readonly removeInfoTooltips: () => void
	private friendsContentEl?: HTMLElement
	private readonly unsubscribeFriendsUpdates: () => void
	private onlyMine = false
	private types = new Set<ActivityType>(ACTIVITY_TYPES)
	private items = new Map<string, ActivityFeedItem>()
	private readonly handleReleaseUpdated = (event: Event): void => {
		const { reason, release } = getReleaseUpdateDetail(event)
		if (reason === 'hydration' || reason === 'details')
			this.applyReleaseSummary(release.data)
	}
	private readonly pendingMetadata = new Set<number>()
	private readonly attemptedMetadata = new Set<number>()
	private nextCursor: string | null = null
	private requestController = new AbortController()
	private loadFailed = false
	private destroyed = false
	private active = false
	readonly ready: Promise<void>
	private refreshSequence = 0
	private expiryTimer?: ReturnType<typeof setTimeout>
	private readonly friendsNoteStorageKey: string
	private friendsNoteDismissed = false

	constructor(rootEl: HTMLElement, userId: string) {
		this.rootEl = rootEl
		this.unsubscribeFriendsUpdates = subscribeFriendsUpdates((data) => {
			const contentEl = this.friendsContentEl
			if (this.destroyed || !this.active || !contentEl?.isConnected)
				return
			const lists = contentEl.querySelector<HTMLElement>(
				'[data-friend-lists]',
			)
			if (!lists) return
			lists.innerHTML = friendsListsTemplate(data)
			this.bindFriendActions(contentEl)
		})
		this.removeInfoTooltips = bindInfoTooltips(rootEl)
		this.friendsNoteStorageKey = `tunetoss:activity-friends-note-dismissed:${userId}`
		try {
			this.friendsNoteDismissed =
				localStorage.getItem(this.friendsNoteStorageKey) === 'true'
		} catch {
			// Keep the notice usable when browser storage is unavailable.
		}
		const listEl = rootEl.querySelector<HTMLOListElement>(
			'[data-activity-list]',
		)
		const statusEl = rootEl.querySelector<HTMLElement>(
			'[data-activity-status]',
		)
		const noFriendsEl =
			rootEl.querySelector<HTMLElement>('[data-no-friends]')
		if (!listEl || !statusEl || !noFriendsEl) {
			throw new Error('Activity page markup is incomplete')
		}
		this.listEl = listEl
		this.statusEl = statusEl
		this.noFriendsEl = noFriendsEl
		this.handleClick = this.handleClick.bind(this)
		this.handleKeydown = this.handleKeydown.bind(this)
		this.rootEl.addEventListener('click', this.handleClick)
		this.rootEl.addEventListener('keydown', this.handleKeydown)
		window.addEventListener(
			'collection:release-updated',
			this.handleReleaseUpdated,
		)
		renderFriendRequestIndicator()
		bindMultiSelectOutsideDismiss()
		this.infiniteList = createInfiniteList<ActivityFeedItem>({
			containerEl: () => this.listEl,
			limit: INFINITE_SCROLL_PAGE_SIZE,
			fetchFunction: async (_offset, limit) => {
				const signal = this.requestController.signal
				const response = await getActivityFeed({
					types: [...this.types],
					onlyMine: this.onlyMine,
					cursor: this.nextCursor ?? undefined,
					limit,
					signal,
				})
				signal.throwIfAborted()
				this.nextCursor = response.nextCursor
				this.noFriendsEl.hidden =
					response.hasFriends || this.friendsNoteDismissed
				return {
					items: response.items,
					hasMore: response.nextCursor !== null,
				}
			},
			renderItem: (item) => {
				this.items.set(item.id, item)
				this.scheduleExpiry()
				this.fillMissingMetadata()
				const fragment = document
					.createRange()
					.createContextualFragment(activityItemTemplate(item))
				return fragment.firstElementChild as HTMLElement
			},
			onFetchingChange: (fetching) => {
				if (this.destroyed) return
				if (fetching) {
					this.loadFailed = false
					this.statusEl.innerHTML = activityLoadingTemplate()
				} else if (!this.loadFailed) {
					this.statusEl.innerHTML =
						this.items.size === 0 && !this.nextCursor
							? activityEmptyTemplate(
									this.types.size !== ACTIVITY_TYPES.length,
								)
							: ''
				}
			},
			onError: () => {
				this.loadFailed = true
				this.statusEl.innerHTML = activityErrorTemplate()
			},
		})
		this.ready = this.infiniteList.start()
	}

	show(): void {
		this.active = true
		this.expireSummaries()
		this.infiniteList.resume()
		this.fillMissingMetadata()
	}

	hide(): void {
		this.active = false
		this.infiniteList.pause()
	}

	async refresh(): Promise<void> {
		await this.reload(true)
	}

	private async reload(preserve: boolean): Promise<void> {
		if (this.destroyed) return
		const sequence = ++this.refreshSequence
		const targetCount = preserve
			? Math.max(this.items.size, INFINITE_SCROLL_PAGE_SIZE)
			: INFINITE_SCROLL_PAGE_SIZE
		this.infiniteList.pause()
		this.infiniteList.cancelPending()
		this.requestController.abort()
		this.requestController = new AbortController()
		const signal = this.requestController.signal
		const items: ActivityFeedItem[] = []
		let cursor: string | null = null
		let hasFriends = true
		try {
			do {
				const response = await getActivityFeed({
					types: [...this.types],
					onlyMine: this.onlyMine,
					cursor: cursor ?? undefined,
					limit: INFINITE_SCROLL_PAGE_SIZE,
					signal,
				})
				signal.throwIfAborted()
				if (this.destroyed || sequence !== this.refreshSequence) return
				items.push(...response.items)
				cursor = response.nextCursor
				hasFriends = response.hasFriends
			} while (cursor && items.length < targetCount)
			this.attemptedMetadata.clear()
			this.items.clear()
			this.nextCursor = cursor
			this.loadFailed = false
			this.noFriendsEl.hidden = hasFriends || this.friendsNoteDismissed
			this.listEl.replaceChildren()
			this.infiniteList.reset({
				initialItems: items,
				hasMore: cursor !== null,
			})
			// Reset notifies before buffered rows render; use the fetched result here.
			this.statusEl.innerHTML = items.length
				? ''
				: activityEmptyTemplate(
						this.types.size !== ACTIVITY_TYPES.length,
					)
			await this.infiniteList.start()
		} catch (error) {
			if (signal.aborted || this.destroyed) return
			if (!preserve) this.statusEl.innerHTML = activityErrorTemplate()
			throw error
		} finally {
			if (this.active && sequence === this.refreshSequence)
				this.infiniteList.resume()
		}
	}

	dispose(): void {
		this.unsubscribeFriendsUpdates()
		this.friendsContentEl = undefined
		this.removeInfoTooltips()
		clearTimeout(this.expiryTimer)
		this.destroyed = true
		this.refreshSequence++
		this.requestController.abort()
		this.infiniteList.destroy()
		this.rootEl.removeEventListener('click', this.handleClick)
		this.rootEl.removeEventListener('keydown', this.handleKeydown)
		window.removeEventListener(
			'collection:release-updated',
			this.handleReleaseUpdated,
		)
	}

	private handleKeydown(event: KeyboardEvent): void {
		if (!handleMultiSelectEscape(this.rootEl, event))
			handleMultiSelectNavigation(event)
	}

	private scheduleExpiry(): void {
		clearTimeout(this.expiryTimer)
		const deadlines = [...this.items.values()]
			.map((item) => Date.parse(item.release.validUntil ?? ''))
			.filter(Number.isFinite)
		if (deadlines.length)
			this.expiryTimer = setTimeout(
				() => this.expireSummaries(),
				Math.max(0, Math.min(...deadlines) - Date.now()),
			)
	}

	private fillMissingMetadata(): void {
		if (!this.active || this.destroyed || this.sessionSignal.aborted) return
		for (const item of this.items.values()) {
			if (this.pendingMetadata.size >= 2) break
			const releaseId = item.release.id
			if (
				item.release.validUntil !== null ||
				this.pendingMetadata.has(releaseId) ||
				this.attemptedMetadata.has(releaseId)
			)
				continue
			this.pendingMetadata.add(releaseId)
			this.attemptedMetadata.add(releaseId)
			void this.loadReleaseSummary(releaseId)
		}
	}

	private async loadReleaseSummary(releaseId: number): Promise<void> {
		try {
			const details = await getReleaseDetails(releaseId)
			if (details) this.applyReleaseSummary(details)
		} catch (error) {
			if (!this.destroyed && !this.sessionSignal.aborted)
				console.warn('Could not load activity release metadata', error)
		} finally {
			this.pendingMetadata.delete(releaseId)
			this.fillMissingMetadata()
		}
	}

	private applyReleaseSummary(details: ClientDiscogsRelease): void {
		if (this.destroyed || this.sessionSignal.aborted) return
		const deadline = Date.parse(details.expiresAt ?? '')
		if (!Number.isFinite(deadline) || deadline <= Date.now()) return
		for (const item of this.items.values()) {
			if (item.release.id !== details.id) continue
			if (Date.parse(item.release.validUntil ?? '') >= deadline) continue
			// Copy public metadata only; ownership and personal state belong to the viewer.
			item.release = {
				...item.release,
				title: details.title,
				artists:
					details.artists?.map(({ name }) => name).filter(Boolean) ??
					[],
				coverImage: details.cover_image || details.thumb || null,
				year: details.year || null,
				uri: details.uri || item.release.uri,
				validUntil: new Date(deadline).toISOString(),
			}
			this.updateItemRow(item)
		}
		this.scheduleExpiry()
	}

	private updateItemRow(item: ActivityFeedItem): void {
		const row = [
			...this.listEl.querySelectorAll<HTMLElement>('[data-activity-id]'),
		].find((element) => element.dataset.activityId === item.id)
		if (row) row.outerHTML = activityItemTemplate(item)
	}

	private expireSummaries(): void {
		if (this.destroyed) return
		let changed = false
		for (const item of this.items.values()) {
			const deadline = Date.parse(item.release.validUntil ?? '')
			if (!Number.isFinite(deadline) || deadline > Date.now()) continue
			item.release = {
				...item.release,
				validUntil: null,
				title: `Release #${item.release.id}`,
				artists: [],
				coverImage: null,
				year: null,
				owned: false,
			}
			this.attemptedMetadata.delete(item.release.id)
			this.updateItemRow(item)
			changed = true
		}
		if (changed && this.active) closeOverlay()
		this.scheduleExpiry()
		this.fillMissingMetadata()
	}

	private handleClick(event: MouseEvent): void {
		const target = event.target
		if (!(target instanceof Element)) return
		if (target.closest('[data-dismiss-friends-note]')) {
			this.friendsNoteDismissed = true
			this.noFriendsEl.hidden = true
			try {
				localStorage.setItem(this.friendsNoteStorageKey, 'true')
			} catch {
				// The notice stays dismissed for this view even without storage.
			}
			return
		}
		const scopeButton = target.closest<HTMLButtonElement>(
			'[data-activity-scope]',
		)
		if (scopeButton) {
			this.onlyMine = !this.onlyMine
			const restoreFocus = document.activeElement === scopeButton
			scopeButton.outerHTML = activityScopeButtonTemplate(this.onlyMine)
			if (restoreFocus)
				this.rootEl
					.querySelector<HTMLButtonElement>('[data-activity-scope]')
					?.focus({ preventScroll: true })
			this.items.clear()
			this.nextCursor = null
			this.infiniteList.reset({ initialItems: [], hasMore: true })
			this.listEl.replaceChildren()
			this.statusEl.innerHTML = activityLoadingTemplate()
			void this.reload(false).catch(() => {})
			return
		}
		if (target.closest('[data-manage-friends]')) {
			this.openFriends()
			return
		}
		if (target.closest('[data-activity-retry]')) {
			this.infiniteList.retry()
			return
		}
		const releaseButton = target.closest<HTMLElement>('[data-release-id]')
		if (releaseButton) {
			this.openRelease(releaseButton)
			return
		}
		const trigger = target.closest<HTMLButtonElement>(
			'.multi-select-trigger',
		)
		if (trigger) {
			toggleMultiSelect(this.rootEl, trigger)
			return
		}
		const option = target.closest<HTMLButtonElement>('.multi-select-option')
		if (option) this.toggleType(option)
	}

	private toggleType(option: HTMLButtonElement): void {
		const control = option.closest<HTMLElement>('.tt-multi-select')
		if (!control) return
		const { values, selectsAll } = getNextMultiSelectSelection(
			control,
			option,
		)
		this.types = new Set(
			values.filter((value): value is ActivityType =>
				ACTIVITY_TYPES.includes(value as ActivityType),
			),
		)
		syncMultiSelect(control, values, selectsAll)
		void this.reload(false).catch(() => {})
	}

	private openRelease(button: HTMLElement): void {
		const itemEl = button.closest<HTMLElement>('[data-activity-id]')
		const item = itemEl
			? this.items.get(itemEl.dataset.activityId ?? '')
			: null
		if (!item) return
		const release = item.release
		openOverlay(
			renderReleaseLazy(
				release.id,
				release.instanceId,
				{
					history: release.history,
					isFavorite: release.isFavorite,
					rating: release.rating,
					preview: {
						artists: release.artists.join(', '),
						coverSrc: release.coverImage ?? '',
						isHidden: false,
						title: release.title,
						year: release.year ? String(release.year) : '',
					},
				},
				{
					link: { text: 'Show on Discogs', url: release.uri },
					expiresAt: release.validUntil
						? Date.parse(release.validUntil)
						: undefined,
				},
				!release.owned,
			),
		)
	}

	private openFriends(): void {
		openOverlay({
			path: '/activity/friends',
			html: friendsOverlayLoadingTemplate(),
			onMount: () => void this.mountFriendsOverlay(),
			options: { link: false },
		})
	}

	private async mountFriendsOverlay(): Promise<void> {
		if (this.destroyed || !this.active) return
		const contentEl = document.querySelector<HTMLElement>(
			'.overlay-inner-content',
		)
		if (!contentEl) return
		try {
			const data = await loadFriendsWithIndicator()
			this.renderFriends(contentEl, data)
		} catch (error) {
			console.error('Failed to load friends', error)
			if (this.destroyed || !this.active || !contentEl.isConnected) return
			contentEl.innerHTML = friendsOverlayErrorTemplate()
			contentEl
				.querySelector('[data-friends-retry]')
				?.addEventListener('click', () => {
					contentEl.innerHTML = friendsOverlayLoadingTemplate()
					void this.mountFriendsOverlay()
				})
		}
	}

	private renderFriends(
		contentEl: HTMLElement,
		data: FriendsResponse,
		error = '',
	): void {
		if (this.destroyed || !this.active || !contentEl.isConnected) return
		this.friendsContentEl = contentEl
		contentEl.innerHTML = friendsOverlayTemplate(data, error)
		const form = contentEl.querySelector<HTMLFormElement>(
			'[data-friend-add-form]',
		)
		form?.addEventListener('submit', (event) => {
			event.preventDefault()
			void this.submitFriend(form, contentEl)
		})
		this.bindFriendActions(contentEl)
	}

	private bindFriendActions(contentEl: HTMLElement): void {
		contentEl
			.querySelectorAll<HTMLButtonElement>('[data-friend-action]')
			.forEach((button) => {
				button.addEventListener(
					'click',
					() => void this.runFriendAction(button, contentEl),
				)
			})
	}

	private async submitFriend(
		form: HTMLFormElement,
		contentEl: HTMLElement,
	): Promise<void> {
		const submit = form.querySelector<HTMLButtonElement>(
			'button[type="submit"]',
		)
		const username = String(new FormData(form).get('username') ?? '').trim()
		if (!username) return
		if (submit?.disabled) return
		if (submit) submit.disabled = true
		let saved = false
		try {
			await addFriend(username)
			saved = true
			this.renderFriends(contentEl, await loadFriendsWithIndicator())
		} catch (error) {
			if (
				this.destroyed ||
				this.sessionSignal.aborted ||
				!contentEl.isConnected
			)
				return
			const code = (error as AxiosError<{ error?: string }>).response
				?.data?.error
			let message = 'The friend request could not be sent.'
			if (code === 'USER_NOT_FOUND') {
				message = 'No Tunetoss user has that exact Discogs username.'
			} else if (code === 'FRIENDSHIP_EXISTS') {
				message = 'A friendship or request already exists.'
			} else if (code === 'CANNOT_ADD_SELF') {
				message = 'You cannot add yourself.'
			}
			this.showFriendError(
				contentEl,
				saved
					? 'Friend request sent, but the list could not be refreshed. Close and reopen Friends to try again.'
					: message,
			)
		} finally {
			if (submit) submit.disabled = false
		}
	}

	private showFriendError(contentEl: HTMLElement, message: string): void {
		if (
			this.destroyed ||
			!this.active ||
			this.sessionSignal.aborted ||
			!contentEl.isConnected
		)
			return
		const errorEl = contentEl.querySelector<HTMLElement>(
			'[data-friend-error]',
		)
		if (!errorEl) return
		errorEl.textContent = message
		errorEl.hidden = false
	}

	private async runFriendAction(
		button: HTMLButtonElement,
		contentEl: HTMLElement,
	): Promise<void> {
		const row = button.closest<HTMLElement>('[data-friendship-id]')
		const id = row?.dataset.friendshipId
		const action = button.dataset.friendAction as
			| 'accept'
			| 'decline'
			| 'cancel'
			| 'remove'
		if (!id) return
		if (
			action === 'remove' &&
			!window.confirm(
				`Remove ${row?.dataset.friendUsername ?? 'this friend'}?`,
			)
		)
			return
		if (button.disabled) return
		button.disabled = true
		let saved = false
		try {
			await updateFriendship(id, action)
			saved = true
			this.renderFriends(contentEl, await loadFriendsWithIndicator())
			await this.refresh()
		} catch (error) {
			if (
				this.destroyed ||
				this.sessionSignal.aborted ||
				!contentEl.isConnected
			)
				return
			console.error('Friend action or refresh failed', error)
			this.showFriendError(
				contentEl,
				saved
					? 'Change saved, but the view could not be refreshed. Close and reopen Friends or refresh the page to try again.'
					: 'That change could not be saved. Please try again.',
			)
		} finally {
			button.disabled = false
		}
	}
}
